import React from "react";
import { SxProps, WhmStack, WhmTypography } from "@securitize/reactjs-whm";
import { ReactComponent as EntityInfoIcon } from "@securitize/assets-sec-ui/dist/icons/svg/entity-info.svg";
import { ReactComponent as DocumentsIcon } from "@securitize/assets-sec-ui/dist/icons/svg/document.svg";
import { ReactComponent as KeyPartiesIcon } from "@securitize/assets-sec-ui/dist/icons/svg/key-parties.svg";

export const EntityOnboardingIcon = {
  entityInformation: (
    <EntityInfoIcon width={24} height={24} fill="currentColor" />
  ),
  documents: <DocumentsIcon width={24} height={24} fill="currentColor" />,
  keyParties: <KeyPartiesIcon width={24} height={24} fill="currentColor" />,
};

/**
 * Renders one row of the onboarding card as two columns: icon+title+subtitle
 * on the left, and the "X need updates" status message inline with `button`
 * on the right; below the md breakpoint the status message and button stack
 * vertically on their own rows. `button` is optional — on the Account page,
 * the caller omits it since the corresponding tab is right there on the page.
 */
export default function EntityOnboardingSection({
  sx,
  icon,
  title,
  subtitle,
  statusMessage,
  button,
}: {
  sx?: SxProps;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  statusMessage?: React.ReactNode;
  button?: React.ReactNode;
}) {
  return (
    <WhmStack
      direction="row"
      flexWrap="wrap"
      justifyContent={{ xs: "flex-start", xl: "space-between" }}
      alignItems="center"
      gap="12px"
      py="24px"
      sx={sx}
    >
      <WhmStack direction="row" flex={1} minWidth="300px" pr="8px">
        <WhmStack pr="12px" pt="4px">
          {icon}
        </WhmStack>

        <WhmStack gap="4px" flex={1}>
          <WhmTypography variant="subtitle1">{title}</WhmTypography>

          {subtitle && (
            <WhmTypography variant="body2" sx={{ color: "text.secondary" }}>
              {subtitle}
            </WhmTypography>
          )}
        </WhmStack>
      </WhmStack>

      {(statusMessage || button) && (
        <WhmStack
          direction={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          width={{ xs: "100%", xl: "auto" }}
          gap="16px"
        >
          {statusMessage && (
            <WhmTypography
              variant="body2"
              // md–xl the stack spans the full row: auto margin pushes the
              // status to the left edge and the button to the right edge
              sx={{
                color: "text.secondary",
                marginRight: { md: "auto", xl: 0 },
              }}
            >
              {statusMessage}
            </WhmTypography>
          )}

          {button}
        </WhmStack>
      )}
    </WhmStack>
  );
}
